import { Inject, Injectable } from '@angular/core'
import { EpoDnepduosControllerService } from '../../../../../api/backend'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { EMPTY, Observable, of } from 'rxjs'
import { FilterCriteriaStorageService } from '../../../../../core/storage/filter-criteria-storage.service'
import { LiberatedAreaActions } from './action-types'
import { catchError, concatMap, filter, map, switchMap, take } from 'rxjs/operators'
import { selectAllItemCollection, selectLiberatedAreaFilterCriteria } from './liberated-area.selectors'
import { PerfectScrollbarActions } from '../../../../../shared/ui/perfect-scrollbar/state/actions-type'
import { StoreKeysEnum } from '../../../../../store/store-keys'
import { ILiberatedAreaFilterCriteria } from './liberated-area.reducers'
import { SortByActions } from '../../../../../shared/ui/sort-by/state/action-types'

@Injectable()
export class LiberatedAreaEffects {

    filter$: Observable<any>
    setStoredOrDefaultCriteria$: Observable<any>
    loadAllLiberatedAreas$: Observable<any>

    fetchData$: Observable<any>
    filterCriteriaAction$: Observable<any>
    itemCollectionAction$: Observable<any>
    sort$: Observable<any>
    updateFilterCriteriaAndFilter$: Observable<any>

    private filterCriteriaStorageKey = '-liberatedAreaFilterCriteria'

    constructor(@Inject(Actions) private actions$: Actions,
                @Inject(EpoDnepduosControllerService) private epoDnepduosControllerService: EpoDnepduosControllerService,
                @Inject(FilterCriteriaStorageService) private filterCriteriaStorageService: FilterCriteriaStorageService,
                @Inject(Store) private store: Store,) {

        this.filter$ = createEffect(() => this.actions$.pipe(
            ofType(LiberatedAreaActions.filter),
            switchMap(() => {
                return this.store.select(selectLiberatedAreaFilterCriteria).pipe(
                    take(1),
                    concatMap(filterCriteria => {
                        this.filterCriteriaStorageService.storeFilterCriteria(this.filterCriteriaStorageKey, filterCriteria)

                        // force data reload
                        filterCriteria.offset = 0

                        return [
                            LiberatedAreaActions.setLiberatedAreaFilterCriteria({
                                filterCriteria: filterCriteria
                            }),
                            PerfectScrollbarActions.filterChanged({
                                key: StoreKeysEnum.AdminLiberatedArea
                            })
                        ]
                    })
                )
            })
        ))

        this.setStoredOrDefaultCriteria$ = createEffect(() => this.actions$.pipe(
            ofType(LiberatedAreaActions.setStoredOrDefaultCriteria),
            switchMap(() => {
                return this.store.select(selectLiberatedAreaFilterCriteria).pipe(
                    take(1),
                    map(filterCriteria => {
                        const storageCriteria = this.filterCriteriaStorageService.readFilterCriteria(this.filterCriteriaStorageKey)
                        const criteria = this.filterCriteriaStorageService.getStoredOrDefaultCriteria(storageCriteria, filterCriteria)

                        return LiberatedAreaActions.setLiberatedAreaFilterCriteria({
                            filterCriteria: criteria
                        })
                    })
                )
            })
        ))

        this.loadAllLiberatedAreas$ = createEffect(() => this.actions$.pipe(
            ofType(LiberatedAreaActions.loadAllLiberatedAreas),
            switchMap(() => {
                return this.store.select(selectAllItemCollection).pipe(
                    take(1),
                    switchMap(allItemCollection => {
                        if (allItemCollection == null) {
                            return this.epoDnepduosControllerService.getAllEpoDnepduos().pipe(
                                take(1),
                                map(response => {
                                    return LiberatedAreaActions.setLiberatedAreaAllItemCollection({
                                        allItemCollection: response.items
                                    })
                                }),
                                catchError(error => {
                                    console.warn('loadAllLiberatedAreas$ failed:', error?.status)
                                    return EMPTY
                                })
                            )
                        } else {
                            return of(LiberatedAreaActions.noAction())
                        }
                    })
                )
            })
        ))

        // ==> Actions called from PerfectScrollbar
        this.fetchData$ = createEffect(() => this.actions$.pipe(
            ofType(PerfectScrollbarActions.fetchOriginData),
            filter((action) => action.key === StoreKeysEnum.AdminLiberatedArea),
            switchMap((action) =>
                this.fetchData().pipe(
                    map(data => PerfectScrollbarActions.postLoadAction({ key: action.key, data })),
                    catchError(() => of(LiberatedAreaActions.noAction())),
                )
            ),
        ))

        this.filterCriteriaAction$ = createEffect(() => this.actions$.pipe(
            ofType(PerfectScrollbarActions.originFilterCriteriaAction),
            filter((action) => action.key === StoreKeysEnum.AdminLiberatedArea),
            map((action) =>
                LiberatedAreaActions.updateFilterCriteria({ filterCriteriaFragment: action.fragment })
            ),
        ))

        this.itemCollectionAction$ = createEffect(() => this.actions$.pipe(
            ofType(PerfectScrollbarActions.setOriginItemCollection),
            filter((action) => action.key === StoreKeysEnum.AdminLiberatedArea),
            map((action) =>
                LiberatedAreaActions.setLiberatedAreaItemCollection({ itemCollection: action.collection })
            ),
        ))

        // --- sort by component
        this.sort$ = createEffect(() => this.actions$.pipe(
            ofType(SortByActions.sort),
            filter((action) => action.key === StoreKeysEnum.AdminLiberatedArea),
            switchMap((action) => {
                return this.store.select(selectLiberatedAreaFilterCriteria).pipe(
                    take(1),
                    concatMap((filterCriteria) => {
                        let sortedBy = filterCriteria.sortedBy
                        let sortDir = filterCriteria.sortDir

                        if (sortedBy === action.sortValue) {
                            sortDir = sortDir === 'asc' ? 'desc' : 'asc'
                        } else {
                            sortedBy = action.sortValue
                            sortDir = 'asc'
                        }

                        return [
                            LiberatedAreaActions.updateFilterCriteriaAndFilter({
                                filterCriteriaFragment: {
                                    sortedBy: sortedBy,
                                    sortDir: sortDir,
                                    offset: 0,
                                }
                            }),
                        ]
                    })
                )
            })
        ))

        this.updateFilterCriteriaAndFilter$ = createEffect(() => this.actions$.pipe(
            ofType(LiberatedAreaActions.updateFilterCriteriaAndFilter),
            map(() => LiberatedAreaActions.filter()),
        ))
    }

    private fetchData(): Observable<any> {
        return this.store.select(selectLiberatedAreaFilterCriteria).pipe(
            take(1),
            switchMap((filterCriteria: ILiberatedAreaFilterCriteria) => {
                return this.epoDnepduosControllerService.getAllEpoDnepduos(
                    filterCriteria.offset,
                    filterCriteria.limit,
                    filterCriteria.sortDir,
                    filterCriteria.sortedBy,
                    filterCriteria.ukopl,
                    filterCriteria.pocpl,
                    filterCriteria.text,
                )
            }),
        )
    }
}